import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress } from '@mui/material';

import Input, {
  Ref as InputRef,
} from 'components/Common/InputComponents/Input';

import {
  RootState,
  AlertState,
  DataProfileRule,
  DataProfileRuleElement,
} from 'types/types';
import NIService from 'services/NIService';
import { showAlert } from 'features/applicationState';
import { selectDashboardById } from 'features/dashboardsState';

import 'assets/styles/MarkerTable.scss';

interface MarkerAddDataProfileProps {
  addDataProfileDisabled?: boolean;
}

export interface MarkerAddDataProfileRef {
  handleDisablePolygonAsDataProfile: (
    addPolygonAsDataProfileDisabled: boolean
  ) => void;
}

const MarkerAddDataProfileBar: React.ForwardRefRenderFunction<
  MarkerAddDataProfileRef,
  MarkerAddDataProfileProps
> = ({ addDataProfileDisabled = false }, ref) => {
  const initValidation = {
    dataProfileName: '',
  };

  const dispatch = useDispatch();

  const { mapPopupState } = useSelector(
    (state: RootState) => state.applicationState
  );

  const dashboard = useSelector((state: RootState) =>
    selectDashboardById(state, 'ni-ran-advanced-map-dashboard')
  );

  const [showDataProfileOptions, setShowDataProfileOptions] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>(initValidation);
  const [addPolygonAsDataProfileDisabled, setAddPolygonAsDataProfileDisabled] =
    useState<boolean>(addDataProfileDisabled ?? false);

  const dataProfileNameRef = useRef<InputRef>(null);

  const handleDisablePolygonAsDataProfile = (
    addPolygonAsDataProfileDisabled: boolean
  ) => setAddPolygonAsDataProfileDisabled(addPolygonAsDataProfileDisabled);

  const handlePolygonAsDataProfileClick = () => {
    if (addPolygonAsDataProfileDisabled) {
      return;
    }
    setShowDataProfileOptions(true);
  };

  const constructProfileRule = (
    geoJson: {
      type: string;
      coordinates: number[][][];
    },
    regionName: string
  ): DataProfileRule => {
    const regionRuleElement: DataProfileRuleElement = {
      field: 'region_name',
      id: 'region_name',
      operator: 'equal',
      type: 'string',
      value: regionName,
      data: {
        attributeType: 'DimensionAttribute',
      },
      input: 'text',
    };

    const value = JSON.stringify(geoJson);
    const dataProfileRuleElement: DataProfileRuleElement = {
      field: "longitude || ' ' || latitude",
      id: "longitude || ' ' || latitude",
      operator: 'in',
      type: 'string',
      value: value,
      data: {
        attributeType: 'GEO_JSON',
      },
    };

    const dataProfileRule: DataProfileRule = {
      condition: 'AND',
      field: null,
      id: null,
      operator: null,
      rules: [dataProfileRuleElement, regionRuleElement],
      type: null,
      value: null,
      data: null,
    };

    return dataProfileRule;
  };

  const getSourceDataElementByTechnology = (technology: 'NR' | 'LTE') => {
    const key =
      Object.keys(dashboard?.descriptor.settings?.technologies ?? {}).find(
        (el: string) => el.toUpperCase().includes(technology)
      ) ?? '';

    if (key) {
      const sourceDataElement = {
        id: dashboard?.descriptor.settings.technologies[key].id,
        name: dashboard?.descriptor.settings.technologies[key].name,
        items: null,
        contextInfo: {
          group: `RAN_${key}`,
        },
      };
      return sourceDataElement;
    }

    return undefined;
  };

  const formatSavePolygonAsDataProfileBody = (): {
    name: string;
    groupName: any;
    rules: DataProfileRule;
  }[] => {
    const dataProfileName = dataProfileNameRef.current?.value;

    const dataProfileRules = constructProfileRule(
      mapPopupState.geoJson,
      mapPopupState.region ?? ''
    );

    const nrSourceDataElement = getSourceDataElementByTechnology('NR');
    const lteSourceDataElement = getSourceDataElementByTechnology('LTE');

    const NRBody = {
      name: `${dataProfileName}_LTE` ?? '',
      groupName: 'Map created polygon',
      rules: dataProfileRules,
      visibility: 'PUBLIC',
      profileSource: 'RULES',
      sourceDataElement: lteSourceDataElement,
      assignedUsers: [],
      sourceDataElementId: lteSourceDataElement?.id,
      dataElementLevelId: 'cell_natural_key',
      profileType: 'DATA',
    };

    const LTEBody = {
      name: `${dataProfileName}_NR` ?? '',
      groupName: 'Map created polygon',
      rules: dataProfileRules,
      visibility: 'PUBLIC',
      profileSource: 'RULES',
      sourceDataElement: nrSourceDataElement,
      assignedUsers: [],
      sourceDataElementId: nrSourceDataElement?.id,
      dataElementLevelId: 'cell_natural_key',
      profileType: 'DATA',
    };

    return [NRBody, LTEBody];
  };

  const handleSaveDataProfileClick = async () => {
    if (isLoading) {
      return;
    }

    const dataProfileName = dataProfileNameRef.current?.value as
      | string
      | undefined;
    if (dataProfileName && dataProfileName.length > 0) {
      setValidationErrors({ ...validationErrors, dataProfileName: '' });
    } else {
      setValidationErrors({
        ...validationErrors,
        dataProfileName: `Data profile name can't be empty`,
      });
      return;
    }

    setIsLoading(true);

    const body = formatSavePolygonAsDataProfileBody();

    try {
      const niService = new NIService();
      const useExistingGroups = true;
      await niService.CreateDataProfiles(body, useExistingGroups);
      const alertData: AlertState = {
        severity: 'success',
        title: 'Data Profile Creation',
        message: `Data Profile has been created successfully!`,
        open: true,
      };
      dispatch(showAlert(alertData));
      setAddPolygonAsDataProfileDisabled(true);
    } catch (error) {
      console.error(error);
      const alertData: AlertState = {
        severity: 'error',
        title: 'Data Profile Creation',
        message: `Unable to create data profile`,
        open: true,
      };
      dispatch(showAlert(alertData));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (fieldName: string) => (value: string | undefined) => {
    switch (fieldName) {
      case 'dataProfileName': {
        if (value && value.length > 0) {
          setValidationErrors({ ...validationErrors, dataProfileName: '' });
        } else {
          setValidationErrors({
            ...validationErrors,
            dataProfileName: `Data profile name can't be empty`,
          });
        }
      }
    }
  };

  useImperativeHandle(ref, () => ({ handleDisablePolygonAsDataProfile }));

  useEffect(() => {
    if (addDataProfileDisabled !== addPolygonAsDataProfileDisabled) {
      setAddPolygonAsDataProfileDisabled(addDataProfileDisabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addDataProfileDisabled]);

  return (
    <React.Fragment>
      <Box
        className={`save-polygon-data-profile ${
          addPolygonAsDataProfileDisabled || showDataProfileOptions
            ? 'save-polygon-data-profile-disabled'
            : ''
        }`}
        onClick={handlePolygonAsDataProfileClick}
      >
        Save polygon as Data Profile
      </Box>
      {showDataProfileOptions && (
        <React.Fragment>
          <Input
            size="small"
            ref={dataProfileNameRef}
            id="outlined-multiline-static"
            placeholder="Data profile name"
            className="data-profile-input-element"
            error={!!validationErrors.dataProfileName}
            disabled={addPolygonAsDataProfileDisabled}
            helperText={validationErrors.dataProfileName}
            handleOnChange={handleChange('dataProfileName')}
          />
          <Button
            className={`save-polygon-btn ${
              addPolygonAsDataProfileDisabled || isLoading
                ? 'save-polygon-btn-disabled'
                : ''
            }`}
            onClick={handleSaveDataProfileClick}
          >
            Save
            {isLoading && (
              <CircularProgress className="save-polygon-spinner" size="14px" />
            )}
          </Button>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default forwardRef<MarkerAddDataProfileRef, MarkerAddDataProfileProps>(
  MarkerAddDataProfileBar
);
